import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import CardView from '../components/CardView';
import { buildApiUrl } from '../config/api';
import '../style/Chatbot.css';

const Recommendations = () => {
  const [messages, setMessages] = useState([
    {
      id: 'welcome',
      role: 'agent',
      type: 'message',
      message:
        "Hi! I'm The Picky Kitty. Tell me about your cat and what you're looking for, and I'll help you find the best food.",
      products: [],
    },
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isWaiting, setIsWaiting] = useState(false);
  const [focusMessageId, setFocusMessageId] = useState(null);
  const messageRefs = useRef({});
  const inputRef = useRef(null);

  const friendlyErrorMessage =
    "Oops! I couldn't reach my whisker-powered brain. Could you try again in a moment?";

  const buildHistoryPayload = (history) =>
    history.map((entry) => ({
      role: entry.role,
      type: entry.type,
      message: entry.message,
    }));

  const sendMessage = async () => {
    const trimmed = inputValue.trim();
    if (!trimmed || isWaiting) {
      return;
    }

    const userMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      type: 'message',
      message: trimmed,
      products: [],
    };

    const optimisticHistory = [...messages, userMessage];
    setMessages(optimisticHistory);
    setInputValue('');
    setIsWaiting(true);
    setFocusMessageId(userMessage.id);

    try {
      const response = await fetch(buildApiUrl('/api/chat'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: trimmed,
          history: buildHistoryPayload(optimisticHistory),
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch chat response');
      }

      const data = await response.json();
      const agentMessage = {
        id: `agent-${Date.now()}`,
        role: 'agent',
        type: data.type || 'message',
        message: data.message || "I'm here to help! Could you tell me a bit more?",
        products: Array.isArray(data.products) ? data.products : [],
      };

      setMessages((prevMessages) => [...prevMessages, agentMessage]);
    } catch (error) {
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          id: `agent-error-${Date.now()}`,
          role: 'agent',
          type: 'message',
          message: friendlyErrorMessage,
          products: [],
        },
      ]);
    } finally {
      setIsWaiting(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    sendMessage();
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };

  const typingIndicator = useMemo(
    () => (
      <div className="chat-message agent typing">
        <img
          className="chat-avatar"
          src="/picky-kitty/profile_pic.PNG"
          alt="The Picky Kitty"
        />
        <div className="chat-message-content">
          <div className="chat-bubble typing-indicator">
            <span />
            <span />
            <span />
          </div>
        </div>
      </div>
    ),
    []
  );

  useEffect(() => {
    if (!isWaiting && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isWaiting]);

  useLayoutEffect(() => {
    if (!focusMessageId) {
      return;
    }
    requestAnimationFrame(() => {
      const messageElement = messageRefs.current[focusMessageId];
      if (messageElement) {
        messageElement.scrollIntoView({
          block: 'start',
          behavior: 'smooth',
        });
      }
      setFocusMessageId(null);
    });
  }, [messages.length, focusMessageId]);

  return (
    <div className="recommendations-chat-page">
      <header className="chat-header">
        <div className="page-header">
          <h1 className="page-title">Ask the Picky Kitty</h1>
        </div>
        <p className="chat-intro">
          Tell the Picky Kitty what you are looking for, let him guide you, and he will give
          you personalized recommendations.
        </p>
      </header>

      <main className="chat-body">
        <div className="chat-messages">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`chat-message ${message.role}`}
              ref={(element) => {
                if (element) {
                  messageRefs.current[message.id] = element;
                } else {
                  delete messageRefs.current[message.id];
                }
              }}
            >
              {message.role === 'agent' && (
                <img
                  className="chat-avatar"
                  src="/picky-kitty/profile_pic.PNG"
                  alt="The Picky Kitty"
                />
              )}
              <div className="chat-message-content">
                <div className="chat-bubble">
                  <p>{message.message}</p>
                </div>
                {message.role === 'agent' &&
                  message.type === 'recommendation' &&
                  Array.isArray(message.products) &&
                  message.products.length > 0 && (
                    <div className="chat-inline-results">
                      <CardView cards={message.products} />
                    </div>
                  )}
              </div>
            </div>
          ))}
          {isWaiting && typingIndicator}
        </div>
      </main>

      <form className="chat-input" onSubmit={handleSubmit}>
        <div className="chat-input-bar">
          <input
            ref={inputRef}
            type="text"
            placeholder={isWaiting ? 'The Picky Kitty is typing...' : 'Ask about your cat...'}
            value={inputValue}
            onChange={(event) => setInputValue(event.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isWaiting}
            aria-label="Chat input"
          />
          <button type="submit" disabled={isWaiting || !inputValue.trim()}>
            Send
          </button>
        </div>
        <p className="chat-input-note">
          The Picky Kitty is powered by AI and can make mistakes.
        </p>
      </form>
    </div>
  );
};

export default Recommendations;